import { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Info, PlusCircle, Warning } from '@phosphor-icons/react'
import ReactTooltip from 'react-tooltip'
import { useTranslation } from 'react-i18next'

import ToggleField from '../FormLegacy/FormFieldsV2/ToggleField'
import SelectField from '../FormLegacy/FormFieldsV2/SelectField'
import TextField from '../FormLegacy/FormFieldsV2/TextField'
import API from '../../api/api'

import { socialLinkServiceOptions, SOCIAL_LINKS_PAGE } from './helpers'

const Advanced = ({
  values,
  handleChange,
  setFieldValue,
  buildingId,
  currentSurvey,
  isEditingDisabled,
}) => {
  const { t } = useTranslation(['surveys'])
  const [socialLinksData, setSocialLinksData] = useState([])

  const socialLinksAvailable = socialLinksData.length
    ? socialLinksData.map((link) => link.service)
    : []

  const filteredSocialLinkOptions = socialLinkServiceOptions.filter((opt) =>
    socialLinksAvailable.includes(opt.value),
  )

  const hasSocialLinks = filteredSocialLinkOptions.length > 0

  useEffect(() => {
    API.fetchSocialLinks(buildingId).then((response) => {
      setSocialLinksData(response.data)
    })
  }, [])

  useEffect(() => {
    if (!buildingId || currentSurvey?.replyTo || values.replyTo) return

    API.getReviewContact(buildingId)
      .then((response) => {
        if (response.data?.email) {
          setFieldValue('replyTo', response.data.email)
        }
      })
      .catch(() => {})
  }, [buildingId])

  const handleToggleChange = (e) => {
    handleChange(e)
    if (!e.target.checked) {
      setFieldValue('socialLinkService', '')
    }
  }

  return (
    <div className="survey-form__content">
      <div>
        <div className="flex">
          <ToggleField
            labelOnRight
            label={t('surveys:advanced.requestPublicReviews')}
            name="responseTrigger"
            value={values.responseTrigger}
            onChange={handleToggleChange}
            disabled={isEditingDisabled}
          />
          <span data-tip data-for="publicReviewTip" className="survey-form__info-icon">
            <Info size={20} />
            <ReactTooltip
              className="survey-form__tooltip"
              id="publicReviewTip"
              multiline
              place="top"
              effect="solid"
            >
              {t('surveys:advanced.publicReviewTip')}
            </ReactTooltip>
          </span>
        </div>
      </div>

      {values.responseTrigger && (
        <div className="survey-form__review-card">
          <div className="subtitle__wrap">
            <div className="survey-form__label">{t('surveys:advanced.selectSocialLinkLabel')}</div>
            <div className="flex-row">
              {hasSocialLinks && (
                <SelectField
                  name="socialLinkService"
                  value={values.socialLinkService}
                  options={filteredSocialLinkOptions}
                  placeholder={t('surveys:advanced.selectSocialLink')}
                  onChange={handleChange}
                  disabled={isEditingDisabled}
                  extraInputClass="survey-form__input"
                  additionalClass="survey-form__field"
                />
              )}
              <a
                className="survey-form__button"
                href={SOCIAL_LINKS_PAGE}
                target="_blank"
                rel="noopener noreferrer"
              >
                <PlusCircle size={16} />
                {t('surveys:advanced.createSocialLink')}
              </a>
            </div>
          </div>

          <div className="subtitle__wrap">
            <div className="survey-form__label">
              {t('surveys:advanced.replyToEmailLabel')}
              <span data-tip data-for="replyToEmailTip" className="survey-form__info-icon">
                <Info size={16} />
                <ReactTooltip
                  className="survey-form__tooltip"
                  id="replyToEmailTip"
                  multiline
                  place="top"
                  effect="solid"
                >
                  {t('surveys:advanced.replyToEmailTip')}
                </ReactTooltip>
              </span>
            </div>
            <TextField
              name="replyTo"
              value={values.replyTo}
              placeholder={t('surveys:advanced.replyToEmailPlaceholder')}
              onChange={handleChange}
              additionalClass="survey-form__input"
              disabled={isEditingDisabled}
            />
            <div className="survey-form__warning survey-form__warning--warning">
              <Warning size={20} />
              <div>{t('surveys:advanced.replyToWarning')}</div>
            </div>
          </div>
        </div>
      )}

      <ToggleField
        labelOnRight
        label={t('surveys:advanced.pushNotification')}
        name="enablePushNotifications"
        value={values.enablePushNotifications}
        onChange={handleChange}
        disabled={['sms', 'email'].includes(values.communicationType) || isEditingDisabled}
      />
    </div>
  )
}

export default Advanced

Advanced.propTypes = {
  values: PropTypes.object,
  handleChange: PropTypes.func,
  setFieldValue: PropTypes.func,
  buildingId: PropTypes.number,
  currentSurvey: PropTypes.object,
  isEditingDisabled: PropTypes.bool,
}
